import type { AssetResponse } from "../generated/api/types.gen";
import { isColorParameter } from "./parameter-label";

/** The saved asset behind a chosen color, when the value came from one. */
export interface SelectedColorAsset {
  id: string;
  name: string;
}

/** What the parameter asset modal writes back into the form for one parameter.
 *
 *  `value` is what the prompt sees. `assetId` and `assetUrl` are the two ways an
 *  image can reach the refinement as a Gemini reference — from the library or
 *  from a one-time upload — and are mutually exclusive. */
export interface ParameterAssetSelection {
  value: string;
  assetId: string | null;
  assetName: string | null;
  assetUrl: string | null;
}

/** A saved asset of any type. A hex-valued one (color or paint) contributes its
 *  hex; an image contributes its name, since the picture itself travels
 *  separately as a reference resolved from `assetId`. */
export function selectionFromAsset(
  asset: AssetResponse,
): ParameterAssetSelection {
  return {
    value: isColorParameter({ value: asset.value }) ? asset.value : asset.name,
    assetId: asset.id,
    assetName: asset.name,
    assetUrl: null,
  };
}

/** An image uploaded without saving it to the library: there is no asset row to
 *  link, so the parameter carries the upload's URL instead. */
export function selectionFromUpload(
  url: string,
  name: string,
): ParameterAssetSelection {
  return { value: name, assetId: null, assetName: name, assetUrl: url };
}

/** A color from the picker; `asset` is set when the user also saved it. */
export function selectionFromColor(
  hex: string,
  asset?: SelectedColorAsset,
): ParameterAssetSelection {
  return {
    value: hex,
    assetId: asset?.id ?? null,
    assetName: asset?.name ?? null,
    assetUrl: null,
  };
}

/** A hand-typed value no longer represents whatever image or asset it came
 *  from, so every link is dropped together. */
export const CLEARED_PARAMETER_ASSET: ParameterAssetSelection = {
  value: "",
  assetId: null,
  assetName: null,
  assetUrl: null,
};
